import { api } from '@/lib/api/client';
import type {
  Client,
  ClientFilters,
  ClientsResponse,
  CreateClientDto,
  UpdateClientDto,
  Employee,
  CreateEmployeeDto,
  UpdateEmployeeDto,
  EmployeesResponse,
  EmployeeFilters,
} from '../types/client.types';

const ENDPOINT = '/clients';
const EMPLOYEES_ENDPOINT = '/employees';

export const clientsApi = {
  // Fetch clients with filters and pagination
  getAll: async (filters: ClientFilters): Promise<ClientsResponse> => {
    const offset = filters.page * filters.limit;
    const params: Record<string, unknown> = {
      offset,
    };

    // Add limit
    params['limit'] = filters.limit;

    // Add type filter
    if (filters.type) {
      params['type'] = filters.type;
    }

    // Add order params if not empty
    if (filters.orderBy && filters.order) {
      params['orderBy'] = filters.orderBy;
      params['order'] = filters.order;
    }

    // Handle search - require minimum 2 characters
    if (filters.s && filters.s.length >= 2) {
      params['s'] = filters.s;
    }

    return api.get<ClientsResponse>(ENDPOINT, { params });
  },

  // Get single client by ID
  getById: async (id: number | string): Promise<Client> => {
    return api.get<Client>(`${ENDPOINT}/${id}`);
  },

  // Create new client
  create: async (data: CreateClientDto): Promise<Client> => {
    return api.post<Client>(ENDPOINT, data);
  },

  // Update client
  update: async (id: number | string, data: UpdateClientDto): Promise<Client> => {
    return api.patch<Client>(`${ENDPOINT}/${id}`, data);
  },

  // Delete client
  delete: async (id: number | string): Promise<void> => {
    return api.delete(`${ENDPOINT}/${id}`);
  },
};

export const employeesApi = {
  // Get employees for a client with optional pagination
  getByClientId: async (
    clientId: number | string,
    filters?: Partial<EmployeeFilters>
  ): Promise<EmployeesResponse> => {
    const params: Record<string, unknown> = { clientId };

    if (filters?.limit) {
      params['limit'] = filters.limit;
    }
    if (filters?.offset !== undefined) {
      params['offset'] = filters.offset;
    }
    if (filters?.orderBy && filters?.order) {
      params['orderBy'] = filters.orderBy;
      params['order'] = filters.order;
    }

    return api.get<EmployeesResponse>(EMPLOYEES_ENDPOINT, { params });
  },

  // Get single employee by ID
  getById: async (id: number | string): Promise<Employee> => {
    return api.get<Employee>(`${EMPLOYEES_ENDPOINT}/${id}`);
  },

  // Create employee
  create: async (data: CreateEmployeeDto): Promise<Employee> => {
    return api.post<Employee>(EMPLOYEES_ENDPOINT, data);
  },

  // Update employee
  update: async (id: number | string, data: UpdateEmployeeDto): Promise<Employee> => {
    return api.patch<Employee>(`${EMPLOYEES_ENDPOINT}/${id}`, data);
  },

  // Delete employee
  delete: async (id: number | string): Promise<void> => {
    return api.delete(`${EMPLOYEES_ENDPOINT}/${id}`);
  },
};
